import React, { useEffect, useRef, useCallback } from 'react';
import './PremiumOffersScroll.css';

// Debug mode - set to false in production
const DEBUG_MODE = false;

interface ScrollState {
  isVideoFixed: boolean;
  scrollOffset: number;
  lastKnownScrollY: number;
}

export const PremiumOffersScroll: React.FC = () => {
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Single source of truth for scroll state
  const scrollStateRef = useRef<ScrollState>({
    isVideoFixed: false,
    scrollOffset: 0,
    lastKnownScrollY: 0
  });
  
  // Animation frame tracking
  const rafRef = useRef<number | null>(null);
  const tickingRef = useRef(false);
  
  // Container measurements (cached)
  const measurementsRef = useRef({
    containerTop: 0,
    videoHeight: 0,
    navHeight: 80,
    initialized: false
  });

  // Initialize measurements once
  const initializeMeasurements = useCallback(() => {
    if (!containerRef.current || !videoWrapperRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    measurementsRef.current = {
      containerTop: rect.top + window.scrollY,
      videoHeight: videoWrapperRef.current.offsetHeight,
      navHeight: 80,
      initialized: true
    };
    
    if (DEBUG_MODE) {
      console.log('Offers measurements initialized:', measurementsRef.current);
    }
  }, []);

  // Batch DOM updates for 60fps performance
  const updateTransforms = useCallback(() => {
    if (!videoWrapperRef.current) return;
    
    const state = scrollStateRef.current;
    
    // Apply transforms using CSS variables for maximum performance
    videoWrapperRef.current.style.setProperty('--scroll-offset', `${state.scrollOffset}px`);
    
    // Add scrolling class during animation
    videoWrapperRef.current.classList.add('scrolling');
    
    // Remove scrolling class after animation frame
    requestAnimationFrame(() => {
      videoWrapperRef.current?.classList.remove('scrolling');
    });
  }, []);

  // Optimized scroll handler
  const handleScroll = useCallback(() => {
    if (!tickingRef.current) {
      tickingRef.current = true;
      
      rafRef.current = requestAnimationFrame(() => {
        if (!measurementsRef.current.initialized) {
          initializeMeasurements();
        }
        
        const { containerTop, videoHeight, navHeight } = measurementsRef.current;
        const scrollY = window.scrollY;
        const state = scrollStateRef.current;
        
        // Calculate the point where video should start following
        const triggerPoint = containerTop - navHeight;
        const videoBottom = containerTop + videoHeight;
        
        // Determine if we should fix the video
        if (scrollY > triggerPoint && scrollY < videoBottom - navHeight) {
          // Video should be fixed - calculate offset
          const offset = scrollY - containerTop;
          state.scrollOffset = offset;
          state.isVideoFixed = true;
          
          // Make video appear fixed using transform
          if (videoWrapperRef.current) {
            videoWrapperRef.current.classList.add('fixed');
          }
        } else {
          // Video should not be fixed
          state.scrollOffset = 0;
          state.isVideoFixed = false;
          
          if (videoWrapperRef.current) {
            videoWrapperRef.current.classList.remove('fixed');
          }
        }
        
        
        // Update last known scroll position
        state.lastKnownScrollY = scrollY;
        
        // Apply all transforms in one batch
        updateTransforms();
        
        tickingRef.current = false;
      });
    }
  }, [initializeMeasurements, updateTransforms]);

  // Set up intersection observer for better performance
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-80px 0px 0px 0px', // Account for navbar height
      threshold: [0, 0.1, 0.9, 1]
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.target === containerRef.current) {
          // Trigger scroll calculation when container is in view
          if (entry.isIntersecting) {
            handleScroll();
          }
        }
      });
    }, observerOptions);

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [handleScroll]);

  // Set up scroll listener
  useEffect(() => {
    // Initialize measurements on mount
    initializeMeasurements();
    
    // Use passive listener for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial calculation
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [handleScroll, initializeMeasurements]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      measurementsRef.current.initialized = false;
      initializeMeasurements();
      handleScroll();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleScroll, initializeMeasurements]);

  return (
    <div 
      ref={containerRef} 
      className={`premium-offers-container ${DEBUG_MODE ? 'premium-offers-debug' : ''}`}
    >
      {/* Video Wrapper - uses transforms for smooth animation */}
      <div
        ref={videoWrapperRef}
        className="premium-offers-video-wrapper"
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          className="premium-offers-video"
        >
          <source src="https://videos.ctfassets.net/hz1mviiqoqof/5f7KvUi1bDZSsQ7sYHowQb/81b13f4425174508f742bc8bd42febf1/filmCollectionHeaderVideo.mp4" type="video/mp4" />
        </video>
      </div>
    </div>
  );
};